import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Plus, Download, Trash2, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, formatError } from "../lib/api";
import { generateInterrogationPDF } from "../lib/interrogationPdf";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const CONDITIONS = [
  ["hta", "Hipertensión Arterial (HTA)"],
  ["tiroides", "Alteraciones Tiroides"],
  ["ansiedad", "Ansiedad / Estrés severo"],
  ["dm", "Diabetes Mellitus"],
  ["autoinmune", "Enfermedad Autoinmune"],
  ["anemia", "Anemia / Déficit hierro"],
  ["dislipemia", "Dislipemia / Colesterol"],
  ["cardio", "Cardiopatía / Arritmias"],
  ["alopecia", "Alopecia (antec. familiar)"],
];

const TRI_SYMPTOMS = [
  ["caida", "Caída difusa / Aguda"],
  ["picores", "Picores / Escamación"],
  ["densidad", "Pérdida de densidad"],
  ["dolor", "Dolor / Tricodinia"],
  ["placas", "Placas alopécicas"],
  ["seborrea", "Seborrea"],
];

const TRI_TX = [
  ["minoxidil", "Minoxidil"],
  ["finasteride", "Finasteride"],
  ["prp", "PRP"],
];

const empty = {
  patient_id: "", area: "MUE", occupation: "",
  chief_complaint: "", history_evolution: "",
  conditions: [], allergies_known: false, allergies_detail: "", habitual_treatment: "", surgeries: "",
  smoking: "no", alcohol: "no",
  tri_symptoms: [], tri_evolution_time: "", tri_previous_tx: [], tri_previous_tx_other: "",
  tri_density: "", tri_pull_test: "",
  bp_systolic: "", bp_diastolic: "", heart_rate: "", o2_sat: "", temperature: "", glucose: "", weight_kg: "",
  physical_findings: "", diagnosis: "", treatment_plan: "",
  consent_given: true,
};

function CheckboxGrid({ options, value, onToggle, cols = 3, name }) {
  const set = new Set(value || []);
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-${cols} gap-2`}>
      {options.map(([id, label]) => (
        <label key={id} className="flex items-center gap-2 cursor-pointer text-sm">
          <input type="checkbox" checked={set.has(id)} onChange={() => onToggle(id)} data-testid={`${name}-${id}`} />
          <span>{label}</span>
        </label>
      ))}
    </div>
  );
}

export default function Interrogations() {
  const { user } = useAuth();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [tplName, setTplName] = useState("");

  const isDoctor = user?.role === "doctor" || user?.role === "admin";

  const load = () => api.get("/interrogations").then(({ data }) => setItems(data));
  const loadTpls = () => api.get("/interrogation-templates").then(({ data }) => setTemplates(data));

  useEffect(() => {
    load();
    if (isDoctor) {
      api.get("/patients").then(({ data }) => setPatients(data.map((p) => ({ id: p.id, name: p.name })))).catch(() => {});
      loadTpls();
    }
    // if arriving with patientId in state, prefill + open
    const pid = location.state?.patientId;
    if (pid && isDoctor) {
      setForm((p) => ({ ...p, patient_id: pid }));
      setOpen(true);
    }
    // eslint-disable-next-line
  }, []);

  // Prefill from pre-consult submission when patient changes
  useEffect(() => {
    if (!isDoctor || !form.patient_id) return;
    api.get(`/pre-interrogations/patient/${form.patient_id}`).then(({ data }) => {
      if (data && data.payload) {
        const pl = data.payload;
        setForm((prev) => ({
          ...prev,
          occupation: prev.occupation || pl.occupation || "",
          chief_complaint: prev.chief_complaint || pl.chief_complaint || "",
          history_evolution: prev.history_evolution || pl.history_evolution || "",
          conditions: prev.conditions?.length ? prev.conditions : (pl.conditions || []),
          allergies_known: prev.allergies_known || !!pl.allergies_known,
          allergies_detail: prev.allergies_detail || pl.allergies_detail || "",
          habitual_treatment: prev.habitual_treatment || pl.habitual_treatment || "",
          surgeries: prev.surgeries || pl.surgeries || "",
          smoking: prev.smoking && prev.smoking !== "no" ? prev.smoking : (pl.smoking || "no"),
          alcohol: prev.alcohol && prev.alcohol !== "no" ? prev.alcohol : (pl.alcohol || "no"),
        }));
        if (data.submitted_at) toast.info(`Pre-consulta recibida el ${new Date(data.submitted_at).toLocaleDateString("es-ES")} · datos precargados`);
      }
    }).catch(() => {});
  }, [form.patient_id, isDoctor]);

  const loadTemplate = (tpl) => {
    if (!tpl) return;
    setForm((prev) => ({ ...prev, ...(tpl.payload || {}), patient_id: prev.patient_id }));
    toast.success(`Plantilla cargada: ${tpl.name}`);
  };

  const saveAsTemplate = async () => {
    const name = tplName.trim();
    if (!name) return toast.error("Nombre de plantilla requerido");
    try {
      const payload = { ...form };
      delete payload.patient_id;
      await api.post("/interrogation-templates", { name, payload });
      toast.success("Plantilla guardada");
      setTplName("");
      loadTpls();
    } catch (err) { toast.error(formatError(err)); }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm("¿Eliminar plantilla?")) return;
    try { await api.delete(`/interrogation-templates/${id}`); loadTpls(); } catch (err) { toast.error(formatError(err)); }
  };

  const toggle = (field, id) => setForm((p) => {
    const set = new Set(p[field] || []);
    if (set.has(id)) set.delete(id); else set.add(id);
    return { ...p, [field]: Array.from(set) };
  });

  const numOrNull = (v) => v === "" || v === null || v === undefined ? null : (typeof v === "string" ? parseFloat(v) : v);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        bp_systolic: numOrNull(form.bp_systolic),
        bp_diastolic: numOrNull(form.bp_diastolic),
        heart_rate: numOrNull(form.heart_rate),
        o2_sat: numOrNull(form.o2_sat),
        temperature: numOrNull(form.temperature),
        glucose: numOrNull(form.glucose),
        weight_kg: numOrNull(form.weight_kg),
        tri_density: form.tri_density || null,
        tri_pull_test: form.tri_pull_test || null,
      };
      // Remove nulls to keep payload clean
      Object.keys(payload).forEach((k) => { if (payload[k] === null || payload[k] === "") delete payload[k]; });
      // But keep patient_id and chief_complaint required
      if (!form.patient_id) return toast.error("Seleccione un paciente");
      if (!form.chief_complaint.trim()) return toast.error("Motivo de consulta requerido");
      payload.patient_id = form.patient_id;
      payload.chief_complaint = form.chief_complaint;

      const { data } = await api.post("/interrogations", payload);
      toast.success("Interrogatorio guardado y firmado");
      setOpen(false);
      setForm(empty);
      load();
      generateInterrogationPDF(data);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este interrogatorio?")) return;
    try {
      await api.delete(`/interrogations/${id}`);
      toast.success("Eliminado");
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="interrogations-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <h1 className="font-display text-2xl sm:text-3xl flex items-center gap-3">
          <ClipboardList className="w-7 h-7 sm:w-8 sm:h-8 text-primary" /> Interrogatorios
        </h1>
        {isDoctor && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90" data-testid="new-interrogation-btn">
                <Plus className="w-4 h-4 mr-2" /> Nuevo interrogatorio
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Historia Clínica / Interrogatorio</DialogTitle>
                <DialogDescription>
                  Formulario estructurado en 6 secciones. Se genera un PDF firmado electrónicamente (HMAC-SHA256 + sello de tiempo).
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={submit} className="space-y-6 pb-20 sm:pb-6">
                {/* Plantillas */}
                {templates.length > 0 && (
                  <section className="rounded-lg border border-border p-3 bg-muted/40">
                    <div className="text-xs font-medium text-muted-foreground mb-2">Cargar plantilla</div>
                    <div className="flex gap-2 flex-wrap">
                      {templates.map((t) => (
                        <div key={t.id} className="inline-flex items-center gap-1 border border-border rounded-full pl-3 pr-1 py-1 bg-background">
                          <button type="button" onClick={() => loadTemplate(t)} className="text-xs" data-testid={`load-tpl-${t.id}`}>{t.name}</button>
                          <button type="button" onClick={() => deleteTemplate(t.id)} className="text-xs text-muted-foreground hover:text-red-700 px-1" title="Eliminar plantilla">×</button>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Paciente + área */}
                <section className="grid sm:grid-cols-[1fr_180px] gap-3">
                  <div>
                    <Label>Paciente *</Label>
                    <Select value={form.patient_id} onValueChange={(v) => setForm((p) => ({ ...p, patient_id: v }))}>
                      <SelectTrigger data-testid="int-patient"><SelectValue placeholder="Seleccione paciente" /></SelectTrigger>
                      <SelectContent>
                        {patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Área de consulta</Label>
                    <Select value={form.area} onValueChange={(v) => setForm((p) => ({ ...p, area: v }))}>
                      <SelectTrigger data-testid="int-area"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MUE">MUE (Urgencias)</SelectItem>
                        <SelectItem value="Tricología">Tricología</SelectItem>
                        <SelectItem value="Ambas">Ambas</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </section>

                {/* Sección 2 */}
                <section className="space-y-2">
                  <h3 className="font-display text-base bg-emerald-700 text-white px-3 py-1 rounded">2. Motivo de consulta y anamnesis</h3>
                  <div>
                    <Label>Motivo principal de consulta *</Label>
                    <Input required value={form.chief_complaint} onChange={(e) => setForm((p) => ({ ...p, chief_complaint: e.target.value }))} data-testid="int-chief" />
                  </div>
                  <div>
                    <Label>Historia / evolución</Label>
                    <Textarea rows={3} value={form.history_evolution} onChange={(e) => setForm((p) => ({ ...p, history_evolution: e.target.value }))} data-testid="int-history" />
                  </div>
                  <div>
                    <Label>Ocupación</Label>
                    <Input value={form.occupation} onChange={(e) => setForm((p) => ({ ...p, occupation: e.target.value }))} data-testid="int-occupation" />
                  </div>
                </section>

                {/* Sección 3 */}
                <section className="space-y-2">
                  <h3 className="font-display text-base bg-emerald-700 text-white px-3 py-1 rounded">3. Antecedentes, alergias y hábitos</h3>
                  <div>
                    <Label className="mb-2 block">Antecedentes personales</Label>
                    <CheckboxGrid options={CONDITIONS} value={form.conditions} onToggle={(id) => toggle("conditions", id)} cols={3} name="int-cond" />
                  </div>
                  <div className="grid sm:grid-cols-[180px_1fr] gap-3 items-end">
                    <div>
                      <Label className="text-xs">Alergias conocidas</Label>
                      <div className="flex items-center gap-3 h-9">
                        <label className="flex items-center gap-1 text-sm cursor-pointer">
                          <input type="radio" checked={!form.allergies_known} onChange={() => setForm((p) => ({ ...p, allergies_known: false }))} data-testid="int-allerg-no" />
                          No
                        </label>
                        <label className="flex items-center gap-1 text-sm cursor-pointer">
                          <input type="radio" checked={form.allergies_known} onChange={() => setForm((p) => ({ ...p, allergies_known: true }))} data-testid="int-allerg-yes" />
                          Sí
                        </label>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Detalle</Label>
                      <Input value={form.allergies_detail} onChange={(e) => setForm((p) => ({ ...p, allergies_detail: e.target.value }))} disabled={!form.allergies_known} data-testid="int-allerg-detail" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label>Tratamiento habitual</Label>
                      <Input value={form.habitual_treatment} onChange={(e) => setForm((p) => ({ ...p, habitual_treatment: e.target.value }))} data-testid="int-tx-hab" />
                    </div>
                    <div>
                      <Label>Cirugías</Label>
                      <Input value={form.surgeries} onChange={(e) => setForm((p) => ({ ...p, surgeries: e.target.value }))} data-testid="int-surgeries" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label>Tabaco</Label>
                      <Select value={form.smoking} onValueChange={(v) => setForm((p) => ({ ...p, smoking: v }))}>
                        <SelectTrigger data-testid="int-smoking"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="no">No</SelectItem><SelectItem value="si">Sí</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Alcohol</Label>
                      <Select value={form.alcohol} onValueChange={(v) => setForm((p) => ({ ...p, alcohol: v }))}>
                        <SelectTrigger data-testid="int-alcohol"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="no">No</SelectItem>
                          <SelectItem value="ocasional">Ocasional</SelectItem>
                          <SelectItem value="diario">Diario</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </section>

                {/* Sección 4 */}
                <section className="space-y-2">
                  <h3 className="font-display text-base bg-emerald-700 text-white px-3 py-1 rounded">4. Evaluación tricológica</h3>
                  <div>
                    <Label className="mb-2 block">Síntomas</Label>
                    <CheckboxGrid options={TRI_SYMPTOMS} value={form.tri_symptoms} onToggle={(id) => toggle("tri_symptoms", id)} cols={2} name="int-tri-sym" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label>Tiempo de evolución</Label>
                      <Input value={form.tri_evolution_time} onChange={(e) => setForm((p) => ({ ...p, tri_evolution_time: e.target.value }))} placeholder="p.ej. 6 meses" data-testid="int-tri-time" />
                    </div>
                    <div>
                      <Label>Otros tratamientos</Label>
                      <Input value={form.tri_previous_tx_other} onChange={(e) => setForm((p) => ({ ...p, tri_previous_tx_other: e.target.value }))} data-testid="int-tri-tx-other" />
                    </div>
                  </div>
                  <div>
                    <Label className="mb-2 block">Tratamientos previos</Label>
                    <CheckboxGrid options={TRI_TX} value={form.tri_previous_tx} onToggle={(id) => toggle("tri_previous_tx", id)} cols={3} name="int-tri-tx" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label>Densidad</Label>
                      <Select value={form.tri_density || "unset"} onValueChange={(v) => setForm((p) => ({ ...p, tri_density: v === "unset" ? "" : v }))}>
                        <SelectTrigger data-testid="int-density"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent><SelectItem value="unset">—</SelectItem><SelectItem value="normal">Normal</SelectItem><SelectItem value="disminuida">Disminuida</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Pull test (signo del tirón)</Label>
                      <Select value={form.tri_pull_test || "unset"} onValueChange={(v) => setForm((p) => ({ ...p, tri_pull_test: v === "unset" ? "" : v }))}>
                        <SelectTrigger data-testid="int-pulltest"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent><SelectItem value="unset">—</SelectItem><SelectItem value="positivo">Positivo (+)</SelectItem><SelectItem value="negativo">Negativo (−)</SelectItem></SelectContent>
                      </Select>
                    </div>
                  </div>
                </section>

                {/* Sección 5 */}
                <section className="space-y-2">
                  <h3 className="font-display text-base bg-emerald-700 text-white px-3 py-1 rounded">5. Constantes vitales y exploración</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    <div><Label className="text-xs">PA sistólica</Label><Input type="number" value={form.bp_systolic} onChange={(e) => setForm((p) => ({ ...p, bp_systolic: e.target.value }))} placeholder="120" data-testid="int-bp-sys" /></div>
                    <div><Label className="text-xs">PA diastólica</Label><Input type="number" value={form.bp_diastolic} onChange={(e) => setForm((p) => ({ ...p, bp_diastolic: e.target.value }))} placeholder="80" data-testid="int-bp-dia" /></div>
                    <div><Label className="text-xs">FC (bpm)</Label><Input type="number" value={form.heart_rate} onChange={(e) => setForm((p) => ({ ...p, heart_rate: e.target.value }))} placeholder="72" data-testid="int-hr" /></div>
                    <div><Label className="text-xs">SatO₂ (%)</Label><Input type="number" value={form.o2_sat} onChange={(e) => setForm((p) => ({ ...p, o2_sat: e.target.value }))} placeholder="98" data-testid="int-sat" /></div>
                    <div><Label className="text-xs">Tª (°C)</Label><Input type="number" step="0.1" value={form.temperature} onChange={(e) => setForm((p) => ({ ...p, temperature: e.target.value }))} placeholder="36.5" data-testid="int-temp" /></div>
                    <div><Label className="text-xs">Glucemia (mg/dL)</Label><Input type="number" value={form.glucose} onChange={(e) => setForm((p) => ({ ...p, glucose: e.target.value }))} placeholder="95" data-testid="int-glucose" /></div>
                    <div><Label className="text-xs">Peso (kg)</Label><Input type="number" step="0.1" value={form.weight_kg} onChange={(e) => setForm((p) => ({ ...p, weight_kg: e.target.value }))} placeholder="70" data-testid="int-weight" /></div>
                  </div>
                  <div>
                    <Label>Hallazgos de exploración física / MUE</Label>
                    <Textarea rows={3} value={form.physical_findings} onChange={(e) => setForm((p) => ({ ...p, physical_findings: e.target.value }))} data-testid="int-findings" />
                  </div>
                </section>

                {/* Sección 6 */}
                <section className="space-y-2">
                  <h3 className="font-display text-base bg-emerald-700 text-white px-3 py-1 rounded">6. Impresión diagnóstica y plan</h3>
                  <div>
                    <Label>Juicio clínico / Diagnóstico</Label>
                    <Textarea rows={2} value={form.diagnosis} onChange={(e) => setForm((p) => ({ ...p, diagnosis: e.target.value }))} data-testid="int-diagnosis" />
                  </div>
                  <div>
                    <Label>Plan terapéutico, prescripción e indicaciones</Label>
                    <Textarea rows={3} value={form.treatment_plan} onChange={(e) => setForm((p) => ({ ...p, treatment_plan: e.target.value }))} data-testid="int-plan" />
                  </div>
                </section>

                <div className="flex items-center gap-2">
                  <input id="int-consent" type="checkbox" checked={form.consent_given} onChange={(e) => setForm((p) => ({ ...p, consent_given: e.target.checked }))} data-testid="int-consent" />
                  <Label htmlFor="int-consent" className="cursor-pointer text-sm">Consentimiento informado otorgado por el paciente</Label>
                </div>

                <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-background/95 backdrop-blur border-t border-border z-10 space-y-2">
                  <div className="flex gap-2">
                    <Input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="Guardar como plantilla..." data-testid="tpl-name" className="h-9" />
                    <Button type="button" variant="outline" size="sm" onClick={saveAsTemplate} disabled={!tplName.trim()} data-testid="tpl-save">Guardar plantilla</Button>
                  </div>
                  <Button type="submit" disabled={saving || !form.patient_id || !form.chief_complaint} className="w-full bg-primary hover:bg-primary/90" data-testid="int-save-btn">
                    {saving ? "Guardando..." : "Guardar interrogatorio y generar PDF"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card>
        <CardHeader><CardTitle className="font-display text-lg">Registros</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {items.length === 0 && <div className="text-sm text-muted-foreground">Sin interrogatorios aún.</div>}
          {items.map((it) => (
            <div key={it.id} className="border border-border rounded-lg p-4 flex flex-wrap justify-between gap-3" data-testid={`int-row-${it.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-emerald-100 text-emerald-700">{it.area}</Badge>
                  <span className="text-xs text-muted-foreground">{it.code} · {new Date(it.created_at).toLocaleString("es-ES")}</span>
                  {it.signature?.short && (
                    <span className="text-[10px] font-mono text-muted-foreground">Firma {it.signature.short}</span>
                  )}
                </div>
                <div className="font-medium mt-1 truncate">{it.chief_complaint}</div>
                <div className="text-xs text-muted-foreground">{user?.role === "patient" ? it.doctor_name : it.patient_name}</div>
              </div>
              <div className="flex gap-2 items-start">
                <Button variant="outline" size="sm" onClick={() => generateInterrogationPDF(it)} data-testid={`int-pdf-${it.id}`}>
                  <Download className="w-4 h-4 mr-1" /> PDF
                </Button>
                {isDoctor && (
                  <Button variant="outline" size="icon" onClick={() => remove(it.id)} data-testid={`int-delete-${it.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
